import React, {
  createContext,
  useCallback,
  useMemo,
  useState,
  useEffect
} from "react";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";

import { usePostTableQuery } from "hooks/queries/usePostTableQuery";

import {
  CASE_TABLE_COLUMNS,
  DEFAULT_SORT
} from "components/CasesTable/CaseManagementTableColumns";
import { NONE, UNKNOWN } from "core/constants";
import { useGetCaseTableColumns } from "hooks/case-management/useGetCaseTableColumns";

// When grouping, pagination is removed so we need to update the rows per page to ensure all data is loaaded.
export const GROUPING_ROWS_PER_PAGE = 100000;
export const DEFAULT_ROWS_PER_PAGE = 50;

export const CaseManagementParamsContext = createContext({
  /* Loading indicator for fetch */
  casesLoading: true,
  columnsLoading: true,

  /* CM Saved table */
  savedTableView: null,
  onSetSavedTableView: () => {},

  /* CM Table */
  tableState: null,
  setTableState: () => {},
  onSetTableStateFromDefault: () => {},
  fetchCases: () => {}
});
CaseManagementParamsContext.displayName = "CaseManagementParamsContext";

export const CaseManagementParamsProvider = ({ children }) => {
  const queryClient = useQueryClient();

  const defaultTableStateStructure = useMemo(
    () => ({
      cases: [],
      sortBy: DEFAULT_SORT,
      hiddenColumns: [],
      columns: CASE_TABLE_COLUMNS,
      columnOrder: [],
      groupBy: [],
      expanded: {},
      filters: {},
      totalPageCount: 0,
      totalRowCount: 0,
      rowsPerPage: DEFAULT_ROWS_PER_PAGE,
      casesCount: 0,
      pageIndex: 0
    }),
    []
  );
  const [savedTableView, setSavedTableView] = useState(null);
  const [tableState, setTableState] = useState({
    ...defaultTableStateStructure
  });

  const transformCaseRecords = cases => {
    const newRecords = [];
    if (!cases) return newRecords;

    for (const item of cases) {
      newRecords.push({
        case_id: item.id,
        project: item.project,
        project_name: item.project_name,
        assignee_first_name: item.assignee_detail?.first_name,
        assignee_last_name: item.assignee_detail?.last_name,
        created_by_first_name: item.reporter?.first_name,
        created_by_last_name: item.reporter?.last_name,
        site_name: item.site_name ?? UNKNOWN,
        machine_id: item.machine ?? UNKNOWN,
        machine_name: item.machine_name ?? UNKNOWN,
        machine_model: item.model_name ?? UNKNOWN,
        manufacturer_name: item.manufacturer_name ?? UNKNOWN,
        model_name: item.model_name ?? UNKNOWN,
        display_id: item.display_id ?? UNKNOWN,
        priority_name: item.priority_name ?? UNKNOWN,
        case_status_name: item.case_status_name ?? UNKNOWN,
        machine_status_name: item.machine_status_name ?? UNKNOWN,
        latest_work_order_status: item.latest_work_order_status,
        case_action_due: item.case_action_due,
        case_action_name: item.case_action_name,
        diagnostic_severity_name: item.diagnostic_severity_name ?? NONE,
        diagnostic_severity_colour: item.diagnostic_severity_colour,
        inspection_severity_name: item.inspection_severity_name ?? NONE,
        inspection_severity_colour: item.inspection_severity_colour ?? NONE,
        component_name: item.component_name ?? UNKNOWN,
        failure_mode_name: item.failure_mode_name ?? UNKNOWN,
        open: item.open,
        open_date: item.open_date,
        last_updated: item.change_date,
        attributes: item.attributes,
        labels: item.labels,
        description: item.description,
        recommendation: item.recommendation
      });
    }

    return newRecords;
  };

  const onSetSavedTableView = useCallback(
    value => {
      setSavedTableView(value);
    },
    [setSavedTableView]
  );

  const onSetTableStateFromDefault = useCallback(
    value => {
      // Set table state with default values
      const formattedValue = { ...defaultTableStateStructure, ...value };
      setTableState(formattedValue);
      onSetSavedTableView(null);
    },
    [defaultTableStateStructure, setTableState, onSetSavedTableView]
  );

  const {
    pageIndex,
    rowsPerPage,
    sortBy,
    filters,
    initialFetch,
    groupBy
  } = tableState;

  const { data, isLoading: casesLoading } = usePostTableQuery({
    pageIndex,
    rowsPerPage,
    sortBy,
    filters,
    initialFetch
  });

  const { loading: columnsLoading } = useGetCaseTableColumns(setTableState);

  const fetchCases = useCallback(
    (keepCurrentSavedTable = false) => {
      if (!keepCurrentSavedTable) onSetSavedTableView(null);

      queryClient.invalidateQueries([
        "cases",
        { pageIndex, rowsPerPage, sortBy, filters, groupBy }
      ]);
    },
    [
      onSetSavedTableView,
      queryClient,
      pageIndex,
      rowsPerPage,
      sortBy,
      filters,
      groupBy
    ]
  );

  useEffect(() => {
    if (!casesLoading && data) {
      // Maps cases to the correct format
      const cases =
        data.results && data.results.length > 0
          ? transformCaseRecords(data.results)
          : [];

      setTableState(state => ({
        ...state,
        initialFetch: false,
        cases: cases,
        casesCount: data.count
      }));
    }
  }, [casesLoading, data, setTableState]);

  return (
    <CaseManagementParamsContext.Provider
      value={{
        DEFAULT_ROWS_PER_PAGE,
        GROUPING_ROWS_PER_PAGE,
        casesLoading,
        columnsLoading,
        savedTableView,
        onSetSavedTableView,
        tableState,
        setTableState,
        onSetTableStateFromDefault,
        fetchCases
      }}
    >
      {children}
    </CaseManagementParamsContext.Provider>
  );
};

CaseManagementParamsProvider.propTypes = {
  children: PropTypes.node
};
